import { z } from 'zod';

import { ClientIdSchema, LoginRequestSchema } from './requests';
import {
  AccountPrivacySchema,
  AgeRangeSchema,
  CommunicationPrefsSchema,
  ContactOutcomeSchema,
  FollowUpKindSchema,
  IdSchema,
  IsoDateSchema,
  MemberSchema,
  PrivacyRequestKindSchema,
  SwapStatusSchema,
  PrivacyRequestSchema,
  VisitorSourceSchema,
} from './resources';

/** Privacidade (LGPD): aceite dos documentos, comunicações, cópia dos dados e pedidos. */

export const TermsAcceptRequestSchema = z
  .object({
    version: z.string().min(1).max(20).meta({
      description: 'A versão aceita — precisa ser a atual (senão 409 `terms_outdated`).',
    }),
  })
  .meta({ id: 'TermsAcceptRequest', description: 'Aceite dos Termos de Uso e da Política de Privacidade.' });

export const CommunicationsPatchSchema = CommunicationPrefsSchema.partial().meta({
  id: 'CommunicationsPatch',
  description: 'Liga ou desliga um canal de lembretes e cobranças.',
});

export const PrivacyRequestCreateSchema = z
  .object({
    id: ClientIdSchema,
    kind: PrivacyRequestKindSchema,
    note: z.string().max(1000).default(''),
  })
  .meta({
    id: 'PrivacyRequestCreate',
    description: 'Pedido do próprio titular, respondido pela administração.',
  });

export const InactiveDeletionRequestSchema = LoginRequestSchema.omit({ remember: true })
  .extend({ note: z.string().max(1000).default('') })
  .meta({
    id: 'InactiveDeletionRequest',
    description:
      'Pedido de exclusão de quem está inativo e não entra mais: o e-mail e a senha (e a igreja) confirmam quem é. ' +
      'Vira um pedido "Exclusão" dos titulares, respondido pela administração.',
  });

export const PrivacyResolveRequestSchema = z
  .object({
    status: z.enum(['done', 'denied']),
    response: z.string().max(1000).default(''),
    anonymize: z.boolean().default(false).meta({
      description: 'Na exclusão atendida: anonimiza o cadastro e apaga o acesso.',
    }),
  })
  .meta({ id: 'PrivacyResolveRequest' });

export const PrivacyResolveResponseSchema = z
  .object({ request: PrivacyRequestSchema, anonymizedMemberId: IdSchema.nullable() })
  .meta({ id: 'PrivacyResolveResponse' });

const ProfileExportSchema = MemberSchema.omit({
  roles: true,
  availability: true,
  blackouts: true,
  spouseId: true,
}).extend({ spouse: z.string() });

export const PersonalDataExportSchema = z
  .object({
    format: z.literal('live-gospel-escalas/dados-pessoais@1'),
    generatedAt: z.iso.datetime(),
    legalVersion: z.string(),
    organization: z.object({ name: z.string(), slug: z.string() }),
    profile: ProfileExportSchema,
    roles: z.array(z.object({ ministry: z.string(), role: z.string(), leader: z.boolean() })),
    availability: z.array(z.object({ day: z.string(), period: z.string(), available: z.boolean() })),
    blackouts: MemberSchema.shape.blackouts,
    schedules: z.array(
      z.object({
        date: IsoDateSchema,
        time: z.string(),
        event: z.string(),
        ministry: z.string(),
        role: z.string(),
        published: z.boolean(),
        response: z.enum(['confirmed', 'declined', 'pending']),
        reason: z.string(),
        attendance: z.enum(['present', 'absent']).nullable(),
      })
    ),
    substitutions: z.array(z.object({ date: IsoDateSchema, event: z.string() })),
    swaps: z.array(
      z.object({
        date: IsoDateSchema,
        event: z.string(),
        role: z.string(),
        direction: z.enum(['sent', 'received']),
        status: SwapStatusSchema,
      })
    ),
    privacyRequests: z.array(PrivacyRequestSchema.omit({ memberId: true })),
    visitorRecord: z
      .object({
        visits: z.array(IsoDateSchema),
        source: VisitorSourceSchema,
        ageRange: AgeRangeSchema,
        district: z.string(),
        wantsContact: z.boolean(),
        consentAt: z.iso.datetime(),
        contacts: z.array(
          z.object({ at: z.iso.datetime(), kind: FollowUpKindSchema, outcome: ContactOutcomeSchema })
        ),
      })
      .nullable()
      .meta({ description: 'A ficha de visitante de onde o cadastro veio.' }),
    account: AccountPrivacySchema.extend({
      createdAt: z.iso.datetime().nullable(),
      activatedAt: z.iso.datetime().nullable(),
      lastLoginAt: z.iso.datetime().nullable(),
    }).nullable(),
    messages: z.array(z.object({ kind: z.string(), at: z.iso.datetime(), channels: z.array(z.string()) })),
  })
  .meta({
    id: 'PersonalDataExport',
    description: 'Cópia dos dados pessoais do titular, em JSON (LGPD, art. 18, II e V).',
  });

export type TermsAcceptRequest = z.infer<typeof TermsAcceptRequestSchema>;
export type CommunicationsPatch = z.infer<typeof CommunicationsPatchSchema>;
export type PrivacyRequestCreate = z.infer<typeof PrivacyRequestCreateSchema>;
export type InactiveDeletionRequest = z.infer<typeof InactiveDeletionRequestSchema>;
export type PrivacyResolveRequest = z.infer<typeof PrivacyResolveRequestSchema>;
export type PrivacyResolveResponse = z.infer<typeof PrivacyResolveResponseSchema>;
export type PersonalDataExportDto = z.infer<typeof PersonalDataExportSchema>;
